import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import MaterialModal from "../../app/material-modal";
import { DatePicker } from "@mui/x-date-pickers";
import { useKeycloak } from "@react-keycloak/web";
import { useAtom } from "jotai";
import { useFormik } from "formik";
import { useQueryClient } from "@tanstack/react-query";
import dayjs, { Dayjs } from "dayjs";
import { toast } from "sonner";
import { SAdminBaseAtom } from "../../../stores/jotai/super-admin/superAdminBase";
import { useParams } from "react-router-dom";
import { useState } from "react";
import { Card, CardContent, Divider } from "@mui/material";
import { useAssignSeat } from "../../../tanstackQuery/queries/superAdmin/invoice";

const AssignSeatModal = ({
  tableRow,
  open,
  handleClose,
}) => {

  const { keycloak } = useKeycloak();
  const client = useQueryClient();
  const { orgID } = useParams();
  const invoicesData = client.getQueryData(["get-invoices",{ orgID }]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const {
    mutateAsync: assignSeat,
    isPending,
    isSuccess,
  } = useAssignSeat(
    { accessToken: keycloak?.token },
    {
      onSuccess: () => {
        client.invalidateQueries({ queryKey: ["orgUserList"]});
        closeAndResetForm();
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      invoice: "",
      startDate: "",
    },
    validate: (values) => {
      const errors: { invoice?: string; startDate?: string } = {};
      if (!values.invoice) {
        errors.invoice = "Invoice is required.";
      }
      if (!values.startDate) {
        errors.startDate = "Start date is required.";
      }
      return errors;
    },
    onSubmit: (values) => {
      handleAssignSeat(values);
    },
  });
  const closeAndResetForm = () => {
    formik.resetForm();
    setSelectedInvoice(null);
    handleClose();
  };
  const handleAssignSeat= async(values)=>{
    const newRoleData = {
      userID: tableRow?.userID,
      orgID,
      invoiceID: selectedInvoice?.invoiceKey,
      startDate: values?.startDate,
      productID:selectedInvoice?.product?.productID,
    };

    try{
      await assignSeat(newRoleData);
      if(isSuccess){
        toast.success("New seat successfully assigned!");
        formik.resetForm();
      }
    }
    catch(error){
      toast.error(error?.response?.data?.message || "Failed to assign seat.");
    }
  }

  return (
    <MaterialModal
      open={open}
      handleDialogClose={closeAndResetForm}
      title="Assign Seat"
      styleOverrides={{
        container: {
          "& .MuiDialog-paper": {
            width: "100%",
            maxWidth: "500px",
            ".MuiDialogContent-root": {
              overflow: "auto",
            },
          },
        },
      }}
    >
      {selectedInvoice && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold">
              Invoice Info
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="body2">
              <strong>Name:</strong> {selectedInvoice.name}
            </Typography>
            <Typography variant="body2">
              <strong>Status:</strong> {selectedInvoice.status}
            </Typography>
            <Typography variant="body2">
              <strong>Type:</strong> {selectedInvoice.type}
            </Typography>
            <Typography variant="body2">
              <strong>Project:</strong> {selectedInvoice.project}
            </Typography>
            <Typography variant="body2">
              <strong>Product:</strong> {selectedInvoice.product?.productName}
            </Typography>
            <Typography variant="body2">
              <strong>Quantity:</strong> {selectedInvoice.product?.quantity}
            </Typography>
            <Typography variant="body2">
              <strong>Used:</strong> {selectedInvoice.product?.usedQuantity}
            </Typography>
          </CardContent>
        </Card>
      )}


      <form onSubmit={formik.handleSubmit}>
        <Stack direction="column" gap={2}>

          <Stack direction="row" gap={3}>
            <Typography variant="body1" sx={{ flexBasis: "30%" }}>
              Select Invoice *
            </Typography>
            <FormControl
              sx={{ minWidth: 300 }}
              size="small"
              error={formik.submitCount > 0 && !formik.values.invoice}
            >
              <Select
                name="invoice"
                value={formik.values.invoice}
                onChange={(event) => {
                  const selectedID = event.target.value;
                  const selected = invoicesData?.find(
                    (inv) => inv?.product?.productID === selectedID
                  );
                  setSelectedInvoice(selected);
                  formik.setFieldValue("invoice", selectedID);
                }}
                onBlur={formik.handleBlur}
                displayEmpty
                fullWidth
              >
                <MenuItem value="" disabled>
                  <em>Select Invoice</em>
                </MenuItem>
                {invoicesData?.map((item) => (
                  <MenuItem
                    key={item?.invoiceKey}
                    value={item?.product?.productID}
                  >
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {formik.touched.invoice && formik.errors.invoice}
              </FormHelperText>
              {/* {formik.submitCount > 0 && !formik.values.invoice && (
                <Typography variant="caption" color="error">
                  Required
                </Typography>
              )} */}
            </FormControl>
          </Stack>


          <Stack direction="row" gap={3}>
            <Typography variant="body1" sx={{ flexBasis: "42%" }}>
              Starting Date*
            </Typography>
            <DatePicker
              value={formik.values.startDate}
              onChange={(newValue) =>
                formik.setFieldValue("startDate", newValue)
              }
              onError={() => {}} 
              slotProps={{
                textField: {
                  size: "small",
                  error: Boolean(
                    formik.touched.startDate && formik.errors.startDate
                  ),
                  helperText:
                    formik.touched.startDate && formik.errors.startDate,
                },
              }}
            />
          </Stack>

          {/* Actions */}
          <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
            <Button variant="outlined" onClick={closeAndResetForm}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isPending}
              endIcon={isPending ? <CircularProgress size={16} /> : null}
            >
              Assign Seat
            </Button>
          </Box>
        </Stack>
      </form>
    </MaterialModal>
  );
};

export default AssignSeatModal;
